import { Box } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import { CustomAppUserApi } from "src/api/generated/erp/customApps/api/customAppUserApi.ts";
import { createCustomAppFrame } from "src/customApps/runtime/customAppFrame.ts";
import {
	createCustomAppHostBridge,
	type CustomAppViewOverride,
	type CustomAppHostBridge,
} from "src/customApps/runtime/customAppHostBridge.ts";
import { createCustomAppWindowManager, type CustomAppWindowContext } from "src/customApps/runtime/customAppWindows.ts";
import type { ConnectedCustomAppsStore, CustomAppAiSource } from "src/customApps/ai/connectedCustomApps.ts";
import { ConnectedCustomAppsContext } from "src/customApps/ai/connectedCustomAppsContext.ts";
import { getSelectedEnvironmentId } from "src/storage/appState/environment.ts";
import { getGoogleMapsBrowserConfiguration } from "src/components/common/googleMaps/googleMapsApi.ts";

interface CustomAppFrameProps {
	input: unknown;
	onRequestClose?: () => void;
	/** Set when Aavo opens the app in place of a view. */
	viewOverride?: CustomAppViewOverride;
	onBridge?: (bridge: CustomAppHostBridge | undefined) => void;
	customAppId: number;
	/** Null for the current user's dev build. */
	customAppRevisionId: number | null;
	/**
	 * Set for the live preview of the current user's dev build, which connects to the Aavo AI conversation that
	 * uploaded it. Other dev build frames are not connected to Aavo AI.
	 */
	devBuild?: { customAppDevBuildId: number; threadId: string };
	dataAccessToken: string;
	framePath: string;
	runtimeOrigin: string;
	title: string;
	/** True when the app declares `device_location:use`, which delegates geolocation to its frame. */
	allowsDeviceLocation: boolean;
	/** The viewer's permissions, with the permissions they include, which the app checks to show or hide content. */
	viewerPermissions: string[];
	/** False while the app's view stays mounted behind another view. Inactive apps cannot be connected to Aavo AI. */
	isActive?: boolean;
	/** Set for a popup opened by another instance of the app. Popups are not connected to Aavo AI. */
	popupWindow?: CustomAppWindowContext;
}

export function CustomAppFrame({
	input,
	onBridge,
	onRequestClose,
	viewOverride,
	customAppId,
	customAppRevisionId,
	devBuild,
	dataAccessToken,
	framePath,
	runtimeOrigin,
	title,
	allowsDeviceLocation,
	viewerPermissions,
	isActive = true,
	popupWindow,
}: CustomAppFrameProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const connectedCustomApps = useContext(ConnectedCustomAppsContext);
	const isActiveRef = useRef(isActive);
	// The app keeps running when these change, and calls the latest ones.
	const onRequestCloseRef = useRef(onRequestClose);
	const viewOverrideRef = useRef(viewOverride);
	useEffect(() => {
		onRequestCloseRef.current = onRequestClose;
		viewOverrideRef.current = viewOverride;
	});
	const aiRegistrationRef = useRef<ReturnType<ConnectedCustomAppsStore["registerTarget"]> | null>(null);
	const devBuildId = devBuild?.customAppDevBuildId;
	const devBuildThreadId = devBuild?.threadId;

	useEffect(() => {
		isActiveRef.current = isActive;
		aiRegistrationRef.current?.setActive(isActive);
	}, [isActive]);

	useEffect(() => {
		if (popupWindow == null && connectedCustomApps == null)
			throw new Error("A main-window Custom App requires the connected Custom Apps context.");
		const windowContext = popupWindow ?? { windowId: crypto.randomUUID(), parentWindowId: null, params: {} };
		const frame = createCustomAppFrame({
			src: new URL(framePath, window.location.origin).href,
			runtimeOrigin,
			title,
			allowsDeviceLocation,
		});
		Object.assign(frame.style, {
			border: "none",
			display: "block",
			height: "100%",
			width: "100%",
		});
		let bridge: CustomAppHostBridge | undefined = undefined;
		const windows = createCustomAppWindowManager({
			customAppId,
			title,
			access: {
				framePath,
				runtimeOrigin,
				customAppRevisionId,
				dataAccessToken,
				input,
				allowsDeviceLocation,
				viewerPermissions,
			},
			environmentId: getSelectedEnvironmentId(),
			windowId: windowContext.windowId,
			onClosed: (windowId) => bridge?.notifyWindowClosed(windowId),
		});
		bridge = createCustomAppHostBridge({
			input,
			onRequestClose: () => onRequestCloseRef.current?.(),
			getViewOverride: () => viewOverrideRef.current,
			frame,
			dataAccessToken,
			refreshDataAccessToken: async () =>
				(customAppRevisionId === null ?
					await CustomAppUserApi.refreshDevBuildDataAccess({ customAppId })
				:	await CustomAppUserApi.refreshDataAccess({ customAppRevisionId })
				).dataAccessToken,
			window: windowContext,
			viewerPermissions,
			windows,
			googleMaps: getGoogleMapsBrowserConfiguration(),
		});
		containerRef.current?.append(frame);
		const aiSource: CustomAppAiSource | null =
			customAppRevisionId !== null ? { type: "revision", customAppRevisionId }
			: devBuildId != null && devBuildThreadId != null ?
				{ type: "devBuild", customAppDevBuildId: devBuildId, threadId: devBuildThreadId }
			:	null;
		const aiRegistration =
			popupWindow == null && aiSource != null ?
				connectedCustomApps?.registerTarget(
					{
						id: crypto.randomUUID(),
						customAppId,
						source: aiSource,
						appName: title,
						ai: bridge.ai,
					},
					isActiveRef.current,
				)
			:	undefined;
		aiRegistrationRef.current = aiRegistration ?? null;
		onBridge?.(bridge);
		return () => {
			onBridge?.(undefined);
			aiRegistration?.unregister();
			aiRegistrationRef.current = null;
			windows.dispose();
			bridge.dispose();
			frame.remove();
		};
	}, [
		input,
		onBridge,
		connectedCustomApps,
		customAppId,
		customAppRevisionId,
		devBuildId,
		devBuildThreadId,
		dataAccessToken,
		framePath,
		runtimeOrigin,
		title,
		allowsDeviceLocation,
		viewerPermissions,
		popupWindow,
	]);

	return <Box ref={containerRef} sx={{ flex: 1, minWidth: 0, minHeight: 0 }} />;
}
